import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { http, errMsg, EDITION_IMAGES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const FEATURES = [
  ["Travel kits", "Pick edition, destination, season, tier and days. The studio researches, web-verifies every venue and typesets the PDF and interactive app."],
  ["Kit Register & imports", "Queue hundreds of destinations, or import your existing PDF guides into Founder's, Standalone or One-off collections."],
  ["Checks before publishing", "Live web verification, fact checks, page-fill checks and a capped AI budget per kit."],
  ["Founder's kits", "Mark places you've been with the ✦ I've been here badge and your Instagram post."],
  ["PB Extras", "Layover kits, checklists, itineraries and bundles, with app editions and 6-month accuracy updates."],
  ["Instagram ready", "Captions, branded post pictures, carousels, stories and reels for every product."],
  ["Bespoke requests", "Collect custom trip requests and build private kits for each client."],
  ["Shop & delivery", "Publish to pbtravelangel.com, then track orders and deliver downloads automatically."],
];

export default function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      setUser(data);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block min-h-screen">
        <img src={EDITION_IMAGES.wine} alt="Vineyard at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0C0B09]/75" />
        <div data-testid="login-about" className="absolute inset-0 flex flex-col justify-center px-14 xl:px-20 rise">
          <div className="eyebrow">PB Travel Angel · Book. Pack. Go.</div>
          <h1 className="font-display text-5xl lg:text-6xl text-white mt-4 leading-tight">Kit Studio</h1>
          <p className="text-white/75 mt-4 max-w-xl">Everything that makes, checks, sells and delivers PB Travel Angel products, in one place.</p>
          <div className="grid grid-cols-2 gap-x-10 gap-y-6 mt-10 max-w-2xl">
            {FEATURES.map(([t, d], i) => (
              <div key={t} className="rise" style={{ animationDelay: `${0.05 * i}s` }}>
                <div className="text-[#C9A847] text-sm font-semibold">{t}</div>
                <div className="text-white/70 text-[13px] leading-relaxed mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center px-8 sm:px-16">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <img src="/logo-full.png" alt="PB Travel Angel" data-testid="login-logo" className="w-48 h-auto mb-6" />
          <div className="eyebrow">Kit Studio</div>
          <h2 className="font-display text-4xl mt-3 mb-10 text-[#2B2A26]">Sign in</h2>
          <label className="text-xs text-stone-500">Email</label>
          <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 mb-5 h-11 bg-white" required />
          <label className="text-xs text-stone-500">Password</label>
          <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 mb-6 h-11 bg-white" required />
          {error && <div data-testid="login-error" className="text-sm text-red-400 mb-4">{error}</div>}
          <Button data-testid="login-submit-button" disabled={busy} className="w-full h-11 rounded-sm bg-[#C9A847] text-white hover:bg-[#B8973A] font-bold">
            {busy ? "Signing in…" : "Enter the studio"}
          </Button>
        </form>
      </div>
    </div>
  );
}
